/**
 * @author Mihail Sablin <mihail@webprogrammist.com>
 * @copyright Copyright (c) 2026, Mihail Sablin
 * @licence http://www.opensource.org/licenses/mit-license.php MIT licence
 */

/**
 * Универсальное модальное окно
 * Поддерживает: строку, HTML-строку, HTMLElement, Template
 */
export default class Modal extends EventTarget {
	// Приватные поля
	#options;
	#isOpen = false;
	#overlay = null;
	#window = null;
	#scrollWrapper = null;
	#contentInner = null;
	#contentWrapper = null;
	#closeBtn = null;
	#closeBottomBtn = null;
	#handleKeydown = null;

	/**
	 * @param {Object} options
	 * @param {string} options.title - Заголовок модалки (опционально)
	 * @param {string|HTMLElement|HTMLTemplateElement|DocumentFragment|Node} options.content - Контент
	 * @param {string} options.className - Дополнительный CSS-класс для модалки
	 * @param {boolean} options.closeOnOverlay - Закрывать по клику на оверлей
	 * @param {boolean} options.closeOnEscape - Закрывать по Escape
	 * @param {boolean} options.showCloseButton - Показывать кнопку "Закрыть" снизу (по умолчанию true)
	 * @param {Function} options.onOpen - Колбэк при открытии
	 * @param {Function} options.onClose - Колбэк при закрытии
	 */
	constructor(options = {}) {
		super();
		this.#options = {
			title: options.title || '',
			content: options.content || '',
			className: options.className || '',
			closeOnOverlay: options.closeOnOverlay !== false,
			closeOnEscape: options.closeOnEscape !== false,
			showCloseButton: options.showCloseButton !== false,
			onOpen: options.onOpen || null,
			onClose: options.onClose || null,
		};

		this.#createModal();
		this.#bindEvents();
	}

	/**
	 * Создаёт DOM-структуру модального окна
	 */
	#createModal() {
		// Удаляем старую модалку
		const oldModal = document.querySelector('.modal-overlay');
		if (oldModal) oldModal.remove();

		// Оверлей
		this.#overlay = document.createElement('div');
		this.#overlay.className = 'modal-overlay';

		// Окно
		this.#window = document.createElement('div');
		this.#window.className = `modal-window ${this.#options.className}`.trim();

		// Кнопка закрытия — в окне
		this.#closeBtn = document.createElement('button');
		this.#closeBtn.className = 'modal-close-btn';
		this.#closeBtn.setAttribute('aria-label', 'Закрыть');
		this.#closeBtn.type = 'button';
		this.#closeBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        `;

		// Скролл-обёртка
		this.#scrollWrapper = document.createElement('div');
		this.#scrollWrapper.className = 'modal-content-scroll';

		// ВНУТРЕННЯЯ ОБЁРТКА для отступов
		this.#contentInner = document.createElement('div');
		this.#contentInner.className = 'modal-content-inner';

		// Контейнер для контента
		this.#contentWrapper = document.createElement('div');
		this.#contentWrapper.className = 'modal-content-wrapper';

		// Собираем: контент во внутреннюю обёртку
		this.#contentInner.appendChild(this.#contentWrapper);

		// ===== Кнопка "Закрыть" снизу =====
		if (this.#options.showCloseButton) {
			this.#closeBottomBtn = document.createElement('button');
			this.#closeBottomBtn.className = 'nav-button modal-close-bottom';
			this.#closeBottomBtn.textContent = '✕ Закрыть';
			this.#closeBottomBtn.type = 'button';
			this.#contentInner.appendChild(this.#closeBottomBtn);
		}

		// Внутреннюю обёртку в скролл
		this.#scrollWrapper.appendChild(this.#contentInner);

		// Собираем окно: кнопка + скролл
		this.#window.appendChild(this.#closeBtn);
		this.#window.appendChild(this.#scrollWrapper);

		// Окно в оверлей
		this.#overlay.appendChild(this.#window);

		// Оверлей в body
		document.body.appendChild(this.#overlay);

		// Заголовок
		if (this.#options.title) {
			const titleEl = document.createElement('h2');
			titleEl.className = 'modal-title';
			titleEl.textContent = this.#options.title;
			this.#contentWrapper.prepend(titleEl);
		}

		// Контент
		this.#setContent(this.#options.content);
	}

	/**
	 * Устанавливает контент модалки
	 * @param {string|HTMLElement|HTMLTemplateElement} content
	 */
	#setContent(content) {
		// Очищаем контейнер (сохраняем заголовок, если есть)
		const titleEl = this.#contentWrapper.querySelector('.modal-title');
		this.#contentWrapper.innerHTML = '';
		if (titleEl) {
			this.#contentWrapper.appendChild(titleEl);
		}

		if (!content) {
			return;
		}

		if (typeof content === 'string') {
			if (content.trim().startsWith('<')) {
				this.#contentWrapper.insertAdjacentHTML('beforeend', content);
			} else {
				const textNode = document.createTextNode(content);
				this.#contentWrapper.appendChild(textNode);
			}
		} else if (content instanceof HTMLTemplateElement) {
			const clone = content.content.cloneNode(true);
			this.#contentWrapper.appendChild(clone);
		} else if (content instanceof HTMLElement) {
			const clone = content.cloneNode(true);
			this.#contentWrapper.appendChild(clone);
		} else if (content instanceof DocumentFragment) {
			const clone = content.cloneNode(true);
			this.#contentWrapper.appendChild(clone);
		} else if (content instanceof Node) {
			this.#contentWrapper.appendChild(content.cloneNode(true));
		} else {
			this.#contentWrapper.textContent = String(content);
		}
	}

	/**
	 * Привязывает события
	 */
	#bindEvents() {
		// Закрытие по клику на крестик
		this.#closeBtn.addEventListener('click', () => this.close());

		// Закрытие по клику на кнопку снизу
		if (this.#closeBottomBtn) {
			this.#closeBottomBtn.addEventListener('click', () => this.close());
		}

		// Закрытие по клику на оверлей
		if (this.#options.closeOnOverlay) {
			this.#overlay.addEventListener('click', (e) => {
				if (e.target === this.#overlay) {
					this.close();
				}
			});
		}

		// Закрытие по Escape
		if (this.#options.closeOnEscape) {
			this.#handleKeydown = (e) => {
				if (e.key === 'Escape' && this.#isOpen) {
					this.close();
				}
			};
			document.addEventListener('keydown', this.#handleKeydown);
		}
	}

	get contentElement() {
		return this.#contentWrapper;
	}

	/**
	 * Открывает модальное окно
	 * @api
	 * @param {string|HTMLElement|HTMLTemplateElement} newContent - Новый контент (опционально)
	 */
	open(newContent = null) {
		if (newContent !== null) {
			this.#setContent(newContent);
		}

		// Показываем
		this.#overlay.style.display = 'flex';
		this.#isOpen = true;

		// Активация анимации
		requestAnimationFrame(() => {
			this.#overlay.classList.add('active');
		});

		// Блокируем скролл
		document.body.style.overflow = 'hidden';

		// Колбэк
		if (typeof this.#options.onOpen === 'function') {
			this.#options.onOpen(this);
		}

		// Триггер события
		const event = new CustomEvent('modal:open', {detail: {modal: this}});
		document.dispatchEvent(event);

		return this;
	}

	/**
	 * Закрывает модальное окно
	 * @api
	 */
	close() {
		if (!this.#isOpen) return this;

		this.#isOpen = false;

		// Анимация закрытия
		this.#overlay.classList.add('closing');

		setTimeout(() => {
			this.#overlay.classList.remove('active');
			this.#overlay.classList.remove('closing');
			this.#overlay.style.display = 'none';

			// Возвращаем скролл
			document.body.style.overflow = '';

			// Колбэк
			if (typeof this.#options.onClose === 'function') {
				this.#options.onClose(this);
			}

			// Триггер события
			const event = new CustomEvent('modal:close', {detail: {modal: this}});
			document.dispatchEvent(event);
		}, 350);

		return this;
	}

	/**
	 * Уничтожает модалку (удаляет из DOM)
	 * @api
	 */
	destroy() {
		this.close();
		if (this.#handleKeydown) {
			document.removeEventListener('keydown', this.#handleKeydown);
		}
		if (this.#overlay && this.#overlay.parentNode) {
			this.#overlay.parentNode.removeChild(this.#overlay);
		}
	}

	/**
	 * Возвращает копию текущих настроек
	 * @api
	 * @returns {Object} Копия настроек
	 */
	getOptions() {
		return {...this.#options};
	}

	/**
	 * Возвращает значение конкретной опции
	 * @api
	 * @param {string} key - Название опции
	 * @returns {*} Значение опции или undefined
	 */
	getOption(key) {
		return this.#options[key];
	}

	/**
	 * Обновляет опции
	 * @api
	 */
	updateOptions(options) {
		Object.assign(this.#options, options);
		if (options.className) {
			this.#window.className = `modal-window ${options.className}`.trim();
		}
		if (options.title !== undefined) {
			this.setTitle(options.title);
		}
		return this;
	}

	/**
	 * Устанавливает заголовок
	 * @api
	 */
	setTitle(title) {
		let titleEl = this.#contentWrapper.querySelector('.modal-title');
		if (title) {
			if (!titleEl) {
				titleEl = document.createElement('h2');
				titleEl.className = 'modal-title';
				this.#contentWrapper.prepend(titleEl);
			}
			titleEl.textContent = title;
		} else if (titleEl) {
			titleEl.remove();
		}
		return this;
	}

	/**
	 * Статический метод для быстрого открытия
	 * @api
	 * @static
	 */
	static show(content, options = {}) {
		const modal = new Modal({...options, content});
		modal.open();
		return modal;
	}
}
